import olMap from 'ol/Map';
import * as olInteraction from 'ol/interaction';
import * as olExtent from 'ol/extent';
import { View } from "ol";
import Zoom from 'ol/control/Zoom';
import Attribution from "ol/control/Attribution";
import proj4 from "proj4";
import { register } from 'ol/proj/proj4';
import { debounce } from "debounce";
import { MapViewStore } from "../MapViewStore";

proj4.defs([
    [
        'EPSG:27700',
        '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.999601 +x_0=400000 +y_0=-100000 +ellps=airy +units=m +no_defs +towgs84=446.448,-125.157,542.060,0.1502,0.2470,0.8421,-20.4894'
    ]
]);

/**
 * Owns the OpenLayers Map + View: projections, interactions, GB extent,
 * unauthenticated zoom clamping and the debounced resolution listener.
 * Other modules subscribe to zoom changes via onZoomChanged.
 */
export class MapCore {

    private _map: olMap;
    private _mapId = "map";
    private _OS_extent: olExtent.Extent = [-941259.18, 6305988.48, 504314.68, 8833289.16];
    private _maxUnauthenticatedZoom: number = 9; //20km
    private _hasTouch: boolean;
    private _zoomChangedHandlers: Array<(zoom: number) => void> = [];

    get olMap(): olMap {
        return this._map;
    }

    get hasTouch(): boolean {
        return this._hasTouch;
    }

    public constructor(
        private _isMember: boolean,
        private _mapStore: MapViewStore
    ) {
        register(proj4);
        this._hasTouch = ('ontouchstart' in window || navigator.msMaxTouchPoints > 0);
        this._map = this.getMap();
        this._mapStore.initMap(this._map, true);
    }

    public onZoomChanged = (handler: (zoom: number) => void): void => {
        this._zoomChangedHandlers.push(handler);
    }

    public setMapView = (): void => {
        let mapSettings = this._mapStore.getMapInfo();

        // only move map if there is no data to zoom to
        if (mapSettings.centre && mapSettings.zoom) {
            this._map.getView().setCenter(mapSettings.centre);
            // stored zoom may be fractional from earlier sessions; snap to a whole level
            this._map.getView().setZoom(Math.round(mapSettings.zoom));
        } else {
            //zoom to defaults if we don't have any on the cookie
            this._map.getView().setCenter([-163269.4924, 6901652]);
            this._map.getView().setZoom(11);
        }
    }

    private getMap = (): olMap => {
        return new olMap({
            interactions: olInteraction.defaults({
                altShiftDragRotate: true,
                pinchRotate: false,
                mouseWheelZoom: false
            }).extend([
                new olInteraction.PinchZoom(),
                new olInteraction.MouseWheelZoom({
                    constrainResolution: true
                })
            ]),
            controls: [
                new Zoom(),
                new Attribution({
                    collapsible: true,
                    collapsed: true
                })
            ],
            target: this._mapId,
            view: this.getView()
        });
    }

    private getView = (): View => {
        let self = this;

        let view = new View({
            projection: 'EPSG:3857',
            center: [-163269.4924, 6901652],
            minZoom: 7,
            maxZoom: 19,
            // tile layers only render correctly at integer zooms; this also snaps
            // interrupted wheel/animated zooms (rapid scrolling) to a whole level
            constrainResolution: true,
            extent: this._OS_extent
        });

        if (!self._isMember) {
            view.setMaxZoom(this._maxUnauthenticatedZoom);
        }

        //listener fired whenever view resolution changes
        view.on("change:resolution", debounce(
            function (e) {
                let zoom = e.target.getZoom();
                for (const handler of self._zoomChangedHandlers) {
                    handler(zoom);
                }

                if (!self._isMember) {
                    // non auth users max zoom allowed is capped
                    if (zoom > self._maxUnauthenticatedZoom) {
                        self._map.getView().setZoom(self._maxUnauthenticatedZoom);
                    }
                }
            }
            , 100));

        return view;
    }
}
